import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Check, XCircle } from 'lucide-react'
import { getCustomers } from '../../api/master'
import { listCustomerPayments, listOpenCustomerInvoices, saveCustomerPayment, voidCustomerPayment } from '../../api/sales'
import ErrorBanner from '../../components/ErrorBanner'
import DefaultAccountSelect from '../../components/DefaultAccountSelect'
import { useToast } from '../../components/Toast.jsx'
import { StatusBadge } from '../../components/PageParts'
import { fmtDay, localToday, parseWallClock, startOfToday } from '../../utils/format'
import { docNo } from '../../utils/docNumbers'
import SalesTabs from './SalesTabs'
import { money, num } from './salesShared'
import { useSession } from '../../session'

const customerLabel = (customer) => {
  const name = `${customer?.first_name || ''} ${customer?.last_name || ''}`.trim()
  return name || customer?.email || customer?.phone || `Customer #${customer?.customer_id}`
}

export default function CustomerPayment() {
  const { has } = useSession()
  const books = has('accounting') // the plan has Accounting: show accounts and journal entries
  const [sp] = useSearchParams()
  const customerIdQS = sp.get('customer_id') || ''
  const focusInvoiceQS = sp.get('invoice_id') || ''
  const { notify } = useToast()

  const [customers, setCustomers] = useState([])
  const [owing, setOwing] = useState({}) // customer_id -> open balance
  const [customerId, setCustomerId] = useState(customerIdQS)
  const [rows, setRows] = useState([])
  const [recent, setRecent] = useState([])
  const [paymentDate, setPaymentDate] = useState(localToday())
  const [method, setMethod] = useState('Transfer')
  const [depositTo, setDepositTo] = useState('')
  const [referenceNo, setReferenceNo] = useState('')
  const [memo, setMemo] = useState('')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [voidingId, setVoidingId] = useState(null)
  const [error, setError] = useState('')

  const loadCustomer = async (id = customerId, focus = '') => {
    if (!id) { setRows([]); setRecent([]); return }
    setLoading(true)
    setError('')
    try {
      const [openRows, paymentRows] = await Promise.all([
        listOpenCustomerInvoices({ customer_id: id }),
        listCustomerPayments({ customer_id: id }),
      ])
      setRows((openRows || []).map((row) => {
        const balance = num(row.balance_amount)
        return { ...row, balance, pay: focus && String(row.invoice_id) === String(focus) ? balance.toFixed(2) : '' }
      }))
      setRecent(paymentRows || [])
    } catch (e) {
      setRows([])
      setRecent([])
      setError(e.message || 'Could not load this customer’s invoices.')
    } finally {
      setLoading(false)
    }
  }

  const loadOwing = async () => {
    const open = await listOpenCustomerInvoices().catch(() => [])
    const totals = {}
    ;(open || []).forEach((r) => { totals[r.customer_id] = num(totals[r.customer_id]) + num(r.balance_amount) })
    setOwing(totals)
    return totals
  }

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const [customerRows, totals] = await Promise.all([getCustomers(), loadOwing()])
        if (cancelled) return
        setCustomers(Array.isArray(customerRows) ? customerRows : [])
        // Start with the customer from the link, or else whoever owes the most.
        const first = customerIdQS || Object.entries(totals).sort((a, b) => b[1] - a[1])[0]?.[0] || ''
        setCustomerId(String(first))
        await loadCustomer(first, focusInvoiceQS)
      } catch (e) {
        if (!cancelled) setError(e.message || 'Could not load customers.')
      }
    })()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const chooseCustomer = (id) => {
    setCustomerId(id)
    loadCustomer(id)
  }

  const [owes, others] = useMemo(() => {
    const sorted = [...customers].sort((a, b) => customerLabel(a).localeCompare(customerLabel(b)))
    return [sorted.filter((c) => num(owing[c.customer_id]) > 0), sorted.filter((c) => !(num(owing[c.customer_id]) > 0))]
  }, [customers, owing])

  const totalPay = rows.reduce((sum, row) => sum + num(row.pay), 0)
  const today = startOfToday()

  const setRowPay = (idx, value) => setRows((prev) => prev.map((row, i) => (i === idx ? { ...row, pay: value } : row)))
  const payAll = () => setRows((prev) => prev.map((row) => ({ ...row, pay: num(row.balance).toFixed(2) })))
  const clearAmounts = () => setRows((prev) => prev.map((row) => ({ ...row, pay: '' })))

  const savePayment = async () => {
    const lines = rows
      .map((row) => ({ invoice_id: row.invoice_id, amount: num(row.pay), balance: num(row.balance), invoice_number: row.invoice_number }))
      .filter((line) => line.amount > 0)
    if (!customerId) { setError('Choose the customer who paid.'); return }
    if (!paymentDate) { setError('Enter the date the money was received.'); return }
    if (!lines.length) { setError('Enter how much was paid against at least one invoice.'); return }
    const over = lines.find((line) => line.amount - line.balance > 0.004)
    if (over) { setError(`That is more than is owed on invoice ${over.invoice_number || over.invoice_id}.`); return }

    setSaving(true)
    setError('')
    try {
      await saveCustomerPayment({ customer_id: Number(customerId), payment_date: paymentDate, method, deposit_account_code: depositTo || undefined, reference_no: referenceNo, memo, lines })
      notify(`Payment of ${money(totalPay)} saved`)
      setReferenceNo('')
      setMemo('')
      await Promise.all([loadCustomer(customerId), loadOwing()])
    } catch (e) {
      setError(e.message || 'Could not save the payment.')
    } finally {
      setSaving(false)
    }
  }

  const voidPayment = async (payment) => {
    const reason = window.prompt(`Why are you voiding payment ${docNo('customerPayment', payment.payment_no)}?`)
    if (!reason || !reason.trim()) return
    setVoidingId(payment.payment_id)
    setError('')
    try {
      await voidCustomerPayment(payment.payment_id, { reason: reason.trim() })
      notify('Payment voided')
      await Promise.all([loadCustomer(customerId), loadOwing()])
    } catch (e) {
      setError(e.message || 'Could not void the payment.')
    } finally {
      setVoidingId(null)
    }
  }

  const selected = customers.find((c) => String(c.customer_id) === String(customerId))

  return (
    <div className="ar-pay-page">
      <SalesTabs />
      <div className="ar-pay-header">
        <div className="ar-pay-title-block">
          <div>
            <h1>Payments</h1>
            <div className="ar-pay-subtitle">Record money a customer has paid you against their invoices.</div>
          </div>
        </div>
      </div>

      {error && <ErrorBanner message={error} />}

      <div className="ar-pay-card">
        <div className="ar-pay-fields">
          <label>
            <span>Customer</span>
            <select value={customerId} onChange={(e) => chooseCustomer(e.target.value)}>
              <option value="">Choose a customer…</option>
              {owes.length > 0 && (
                <optgroup label="Owe you money">
                  {owes.map((c) => <option key={c.customer_id} value={c.customer_id}>{customerLabel(c)} ({money(owing[c.customer_id])})</option>)}
                </optgroup>
              )}
              <optgroup label={owes.length ? 'Everyone else' : 'Customers'}>
                {others.map((c) => <option key={c.customer_id} value={c.customer_id}>{customerLabel(c)}</option>)}
              </optgroup>
            </select>
          </label>
          <label>
            <span>Date received</span>
            <input type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} />
          </label>
          <label>
            <span>Paid by</span>
            <select value={method} onChange={(e) => setMethod(e.target.value)}>
              <option>Transfer</option>
              <option>Cash</option>
              <option>Card</option>
              <option>Check</option>
            </select>
          </label>
          {books && (
            <label>
              <span>Deposit to</span>
              <DefaultAccountSelect use="in" method={method} value={depositTo} onChange={setDepositTo} />
            </label>
          )}
          <label>
            <span>Reference (optional)</span>
            <input value={referenceNo} onChange={(e) => setReferenceNo(e.target.value)} placeholder="Bank reference or cheque number" />
          </label>
          <label className="ar-pay-field-wide">
            <span>Note (optional)</span>
            <input value={memo} onChange={(e) => setMemo(e.target.value)} placeholder="Only you will see this" />
          </label>
        </div>
      </div>

      <div className="ar-pay-card">
        <div className="ar-pay-card-head">
          <div>
            <h2>Which invoices does it pay?</h2>
            <p>{selected ? `Unpaid invoices for ${customerLabel(selected)}.` : 'Choose a customer to see what they owe.'}</p>
          </div>
          {rows.length > 0 && (
            totalPay > 0
              ? <button type="button" className="ci-btn ci-btn-ghost ci-btn-sm" onClick={clearAmounts}>Clear amounts</button>
              : <button type="button" className="ci-btn ci-btn-ghost ci-btn-sm" onClick={payAll}>Pay everything</button>
          )}
        </div>
        <div className="ar-pay-table-wrap">
          <table className="ar-pay-table">
            <thead>
              <tr>
                <th>Invoice</th>
                <th>Date</th>
                <th>Due</th>
                <th className="num">Still owed</th>
                <th className="num">Amount paid</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="6"><div className="ci-empty">Loading…</div></td></tr>
              ) : !customerId ? (
                <tr><td colSpan="6"><div className="ci-empty">Choose a customer above.</div></td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan="6"><div className="ci-empty">This customer doesn’t owe anything.</div></td></tr>
              ) : rows.map((row, idx) => {
                const due = parseWallClock(row.due_date)
                const overdue = due && due < today
                return (
                  <tr key={row.invoice_id}>
                    <td><Link className="ci-je-link" to={`/sales/customer-invoices/${row.invoice_id}`}>{row.invoice_number || 'Invoice'}</Link></td>
                    <td>{fmtDay(row.invoice_date) || '–'}</td>
                    <td className={overdue ? 'ci-overdue-cell' : undefined}>{fmtDay(row.due_date) || '–'}{overdue ? ' · overdue' : ''}</td>
                    <td className="num ar-pay-balance">{money(row.balance)}</td>
                    <td className="num">
                      <input className="ar-pay-amount" value={row.pay} inputMode="decimal" onChange={(e) => setRowPay(idx, e.target.value)} placeholder="0.00" aria-label={`Amount paid on ${row.invoice_number || row.invoice_id}`} />
                    </td>
                    <td><button type="button" className="ci-btn ci-btn-ghost ci-btn-sm" onClick={() => setRowPay(idx, num(row.balance).toFixed(2))}>Full amount</button></td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        <div className="ar-pay-footer">
          <div className="ar-pay-total">
            <span>Total received</span>
            <strong>{money(totalPay)}</strong>
          </div>
          <button type="button" className="ci-btn ci-btn-primary" onClick={savePayment} disabled={saving || totalPay <= 0}>
            <Check size={14} />
            {saving ? 'Saving…' : 'Save payment'}
          </button>
        </div>
      </div>

      {customerId && (
        <div className="ar-pay-card">
          <div className="ar-pay-card-head">
            <div>
              <h2>Earlier payments</h2>
              <p>{selected ? `What ${customerLabel(selected)} has paid before.` : ''}</p>
            </div>
          </div>
          <div className="ar-pay-table-wrap">
            <table className="ar-pay-table">
              <thead>
                <tr>
                  <th>Payment</th>
                  <th>Date</th>
                  <th>Paid by</th>
                  <th>Reference</th>
                  <th className="num">Amount</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {recent.length === 0 ? (
                  <tr><td colSpan="7"><div className="ci-empty">No payments yet.</div></td></tr>
                ) : recent.map((payment) => {
                  const isVoid = String(payment.status || '').toLowerCase() === 'void'
                  return (
                    <tr key={payment.payment_id}>
                      <td>{docNo('customerPayment', payment.payment_no)}</td>
                      <td>{fmtDay(payment.payment_date) || '–'}</td>
                      <td>{payment.method || '–'}</td>
                      <td>{payment.reference_no || '–'}</td>
                      <td className="num ar-pay-balance">{money(payment.total_amount)}</td>
                      <td><StatusBadge label={isVoid ? 'Void' : 'Received'} tone={isVoid ? 'bad' : 'good'} /></td>
                      <td className="num">
                        {!isVoid && (
                          <button type="button" className="ci-form-danger ci-btn-sm" onClick={() => voidPayment(payment)} disabled={voidingId === payment.payment_id}>
                            <XCircle size={13} />
                            {voidingId === payment.payment_id ? 'Voiding…' : 'Void'}
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
